import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { signInWithRedirect, fetchAuthSession, signOut } from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import ConnectingDialog from '../../ui/dialog/ConnectingDialog';
import AuthMessageScreen from './AuthMessageScreen';
import { ROLE_SUPER_ADMIN, RO_ID } from '../configuration/actions'
import { supportedLanguage } from '../../i18nProvider'
import {
    storeUserInfo,
    performLogout,
    getCognitoLoginLang,
    getUserId,
    roleFromClaims,
} from '../../auth/tokenStore';


// ============================================================================
// Cognito flow (Amplify managed login). Amplify is configured at runtime by
// TenantBootstrap with the tenant's app client id (from the tenant config).
// ============================================================================

// Amplify's code-for-token exchange takes a couple of seconds at most.
const SIGN_IN_COMPLETE_TIMEOUT_MS = 10000;

const INVALID_CALLBACK_TEXTS = {
    fr: { title: 'Cette page de connexion n\'est plus valide', action: 'Se connecter' },
    en: { title: 'This sign-in page is no longer valid', action: 'Sign in' },
};

const START_FAILED_TEXTS = {
    fr: { title: 'La connexion n\'a pas pu démarrer', body: 'Veuillez réessayer.', action: 'Réessayer' },
    en: { title: 'Sign-in could not start', body: 'Please try again.', action: 'Try again' },
};

// 1 : redirect to the Cognito managed login page (Amplify handles PKCE/state)
const CognitoLogin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [attempt, setAttempt] = useState(0);
    const [failed, setFailed] = useState(false);
    // Sent by the signed-out page: the user wants to sign in from THIS tab.
    // Whatever OAuth transaction another tab left in shared storage must go
    // first, otherwise signInWithRedirect blocks on its in-flight flag.
    const takeOver = !!location.state?.takeOver;

    useEffect(() => {
        const start = async () => {
            if (takeOver) {
                // A login completed elsewhere meanwhile (tokens are shared):
                // enter the app rather than sign it out. Read from the
                // user-info cache — fetchAuthSession() would block while
                // another tab's login is in flight.
                if (getUserId()) {
                    navigate('/dashboard', { replace: true });
                    return;
                }
                // signOut() is Amplify's public way to clear the OAuth
                // transaction; with no session it redirects nowhere.
                await signOut().catch(() => { });
            }
            await signInWithRedirect({ options: { lang: getCognitoLoginLang() } });
        };

        start().catch((err) => {
            // Already signed in — go straight to the app
            if (err?.name === 'UserAlreadyAuthenticatedException') {
                navigate('/dashboard', { replace: true });
                return;
            }
            // Amplify not configured, bad OAuth config... Must be visible: the
            // alternative is an endless "connecting" dialog.
            console.error('signInWithRedirect failed', err);
            setFailed(true);
        });
    }, [navigate, takeOver, attempt])

    if (failed) {
        const texts = START_FAILED_TEXTS[getCognitoLoginLang()] || START_FAILED_TEXTS.en;
        return (
            <AuthMessageScreen
                title={texts.title}
                body={texts.body}
                action={texts.action}
                onAction={() => { setFailed(false); setAttempt((n) => n + 1); }}
            />
        );
    }

    return (<ConnectingDialog open={true} />);
}

// 2 : Amplify completes the code-for-token exchange on this page; wait for
// the session, then run the app's own login logic (tenant gate, user info).
// A callback Amplify cannot complete (Cognito error, state mismatch, timeout)
// ends on an explicit screen — never an automatic restart of the login,
// which would loop for as long as Cognito keeps failing.
const CognitoLoginRedirect = () => {
    const navigate = useNavigate();
    const [invalid, setInvalid] = useState(false);

    useEffect(() => {
        let done = false;

        const fail = (reason) => {
            if (done) return;
            done = true;
            console.error('Sign-in callback failed', reason);
            setInvalid(true);
        };

        const complete = async () => {
            if (done) return;
            let session;
            try {
                session = await fetchAuthSession();
            } catch {
                return; // transient — next poll retries
            }
            const claims = session.tokens?.accessToken?.payload;
            if (!claims || done) return;
            done = true;
            cognitoLoginSuccessWrapper({ claims, navigate });
        };

        const unsubscribe = Hub.listen('auth', ({ payload }) => {
            if (payload.event === 'signInWithRedirect') complete();
            if (payload.event === 'signInWithRedirect_failure') fail(payload.data);
        });

        // Nothing to exchange (no code, no error — e.g. the callback URL
        // restored by a mobile web view after the app was closed): Amplify
        // stays silent, so do not wait for the timeout.
        const params = new URLSearchParams(window.location.search);
        if (!params.get('code') && !params.get('error')) {
            fail('no code in callback URL');
            return () => { done = true; unsubscribe(); };
        }

        // The exchange may already have finished before this component
        // mounted — check immediately, then poll as a fallback (the poll also
        // catches a login completed from another tab: tokens are shared).
        complete();
        const poll = setInterval(complete, 750);
        const timeout = setTimeout(() => fail('timeout'), SIGN_IN_COMPLETE_TIMEOUT_MS);

        return () => {
            done = true;
            unsubscribe();
            clearInterval(poll);
            clearTimeout(timeout);
        };
    }, [navigate]);

    if (invalid) {
        const texts = INVALID_CALLBACK_TEXTS[getCognitoLoginLang()] || INVALID_CALLBACK_TEXTS.en;
        return (
            <AuthMessageScreen
                title={texts.title}
                body={texts.body}
                action={texts.action}
                onAction={() => navigate('/init-login', { replace: true, state: { takeOver: true } })}
            />
        );
    }

    return (<ConnectingDialog open={true} />);
}

const cognitoLoginSuccessWrapper = ({ claims, navigate }) => {

    const userName = claims.user_name
    const lang = claims.language
    const userId = claims.userId
    const role = roleFromClaims(claims)

    // Tenant gate: the user's root organization (rootOrganizationId claim,
    // from the Pre-Token-Generation Lambda) must be THIS domain's tenant.
    // Defense in depth/UX only — the authoritative gate is the
    // Pre-Authentication Lambda server-side. Super admins may log in on any
    // tenant domain.
    const tenantRootOrganizationId = localStorage.getItem(RO_ID);

    if (
        role === ROLE_SUPER_ADMIN ||
        (tenantRootOrganizationId && claims.rootOrganizationId === tenantRootOrganizationId)
    ) {
        storeUserInfo({
            userId,
            role,
            userName,
            language: lang && supportedLanguage(lang) ? supportedLanguage(lang) : null,
        });

        // The user's organization is resolved server-side once per login when
        // react-admin resolves permissions (see ensureUserOrganization).

        setTimeout(() => {
            navigate("/dashboard", { replace: true })
        }, 100)
    } else {
        // Wrong tenant for this domain — sign out (performLogout redirects
        // through Cognito's /logout, clearing the managed-login session)
        setTimeout(() => {
            performLogout();
        }, 5000)
    }
}


export const Login = CognitoLogin;
export const LoginRedirect = CognitoLoginRedirect;

export const LoginPage = () => {
    const navigate = useNavigate();

    useEffect(() => {
        navigate('/init-login', { replace: true });
    }, [navigate])

    return (<ConnectingDialog open={true} />);
}
